import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useFocusTrap } from '../lib/useFocusTrap';
import {
  Search,
  LayoutDashboard,
  CalendarDays,
  Send,
  BarChart3,
  MessageSquare,
  AudioLines,
  FileText,
  Settings,
  CircleHelp,
  Link2,
  Plug,
  UserPlus,
  Globe,
  Sparkles,
  MessageCircle,
  FolderPlus,
  FolderKanban,
  ContactRound,
  ListTodo,
  Handshake,
  Bell,
} from 'lucide-react';
import { cn } from '@iheart/ui';
import { brand } from '@iheart/shared';
import { useSearch, type SearchResult } from '../lib/search';

const RESULT_ICON: Record<SearchResult['type'], typeof ListTodo> = {
  task: ListTodo,
  project: FolderKanban,
  chat: MessageCircle,
  notification: Bell,
};

/* ── Types ──────────────────────────────────────────────────────── */

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: typeof Search;
  section: string;
  shortcut?: string;
  action: () => void;
}

/* ── Hook: global keyboard listener ─────────────────────────────── */

export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Cmd/Ctrl + K opens the palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      // Escape closes
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return { open, setOpen };
}

/* ── Hook: global keyboard shortcuts for navigation ─────────────── */

export function useGlobalShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Ignore if user is in an input/textarea/contenteditable
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) {
        return;
      }

      // Only respond to key combos with G prefix (go-to shortcuts)
      // Pattern: press G then a letter within 500ms
      if (!e.metaKey && !e.ctrlKey && !e.altKey) return;

      // Ctrl/Cmd + Shift + <key> shortcuts
      if (e.shiftKey && (e.metaKey || e.ctrlKey)) {
        switch (e.key.toLowerCase()) {
          case 'h':
            e.preventDefault();
            navigate('/dashboard');
            break;
          case 'c':
            e.preventDefault();
            navigate('/calendar');
            break;
          case 'p':
            e.preventDefault();
            navigate('/publish');
            break;
          case 'a':
            e.preventDefault();
            navigate('/analytics');
            break;
          case 'e':
            e.preventDefault();
            navigate('/engage');
            break;
          case 's':
            e.preventDefault();
            navigate('/settings');
            break;
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);
}

/* ── Command items builder ──────────────────────────────────────── */

function useCommands(onClose: () => void): CommandItem[] {
  const navigate = useNavigate();

  return useMemo(() => {
    function go(path: string) {
      return () => {
        navigate(path);
        onClose();
      };
    }

    return [
      // Navigation
      {
        id: 'nav-dashboard',
        label: 'Go to Dashboard',
        icon: LayoutDashboard,
        section: 'Navigation',
        shortcut: '⌘⇧H',
        action: go('/dashboard'),
      },
      {
        id: 'nav-calendar',
        label: 'Go to Calendar',
        icon: CalendarDays,
        section: 'Navigation',
        shortcut: '⌘⇧C',
        action: go('/calendar'),
      },
      {
        id: 'nav-publish',
        label: 'Go to Publish',
        icon: Send,
        section: 'Navigation',
        shortcut: '⌘⇧P',
        action: go('/publish'),
      },
      {
        id: 'nav-analytics',
        label: 'Go to Analytics',
        icon: BarChart3,
        section: 'Navigation',
        shortcut: '⌘⇧A',
        action: go('/analytics'),
      },
      {
        id: 'nav-engage',
        label: 'Go to Engage',
        icon: MessageSquare,
        section: 'Navigation',
        shortcut: '⌘⇧E',
        action: go('/engage'),
      },
      {
        id: 'nav-listening',
        label: 'Go to Listening',
        icon: AudioLines,
        section: 'Navigation',
        action: go('/listening'),
      },
      {
        id: 'nav-reports',
        label: 'Go to Reports',
        icon: FileText,
        section: 'Navigation',
        action: go('/reports'),
      },
      {
        id: 'nav-projects',
        label: 'Go to Projects',
        icon: FolderKanban,
        section: 'Navigation',
        action: go('/projects'),
      },
      {
        id: 'nav-crm',
        label: 'Go to CRM',
        icon: ContactRound,
        section: 'Navigation',
        action: go('/crm'),
      },
      {
        id: 'nav-chat',
        label: 'Go to Team Chat',
        icon: MessageCircle,
        section: 'Navigation',
        action: go('/chat'),
      },
      {
        id: 'nav-settings',
        label: 'Go to Settings',
        icon: Settings,
        section: 'Navigation',
        shortcut: '⌘⇧S',
        action: go('/settings'),
      },
      {
        id: 'nav-resources',
        label: 'Go to Resources & Help',
        icon: CircleHelp,
        section: 'Navigation',
        action: go('/resources'),
      },

      // Actions
      {
        id: 'action-create-post',
        label: 'Create new post',
        description: 'Open the post composer',
        icon: Send,
        section: 'Actions',
        action: go('/publish'),
      },
      {
        id: 'action-connect',
        label: 'Connect social account',
        description: 'Link a new social platform',
        icon: Plug,
        section: 'Actions',
        action: go('/settings/integrations'),
      },
      {
        id: 'action-invite',
        label: 'Invite team member',
        description: 'Add someone to your workspace',
        icon: UserPlus,
        section: 'Actions',
        action: go('/settings/team'),
      },
      {
        id: 'action-workspace',
        label: 'New workspace',
        description: 'Create a client workspace',
        icon: FolderPlus,
        section: 'Actions',
        action: go('/settings/workspace'),
      },
      {
        id: 'action-bio',
        label: 'Set up Link in Bio',
        description: 'Create your branded link page',
        icon: Globe,
        section: 'Actions',
        action: go('/settings/bio'),
      },
      {
        id: 'action-ai',
        label: 'AI Content Suggestions',
        description: 'Get AI-powered post ideas',
        icon: Sparkles,
        section: 'Actions',
        action: go('/publish/suggestions'),
      },
      {
        id: 'action-links',
        label: 'Manage short links',
        description: 'Create and track short URLs',
        icon: Link2,
        section: 'Actions',
        action: go('/settings/links'),
      },
      {
        id: 'action-new-project',
        label: 'New project',
        description: 'Create a project from a template',
        icon: FolderKanban,
        section: 'Actions',
        action: go('/projects?new=true'),
      },
      {
        id: 'action-new-task',
        label: 'New task',
        description: 'Create a standalone task',
        icon: ListTodo,
        section: 'Actions',
        action: go('/projects'),
      },
      {
        id: 'action-new-contact',
        label: 'New CRM contact',
        description: 'Add a client or lead to CRM',
        icon: ContactRound,
        section: 'Actions',
        action: go('/crm/contacts?new=true'),
      },
      {
        id: 'action-new-deal',
        label: 'New deal',
        description: 'Track a sales opportunity',
        icon: Handshake,
        section: 'Actions',
        action: go('/crm/deals'),
      },
    ];
  }, [navigate, onClose]);
}

/* ── CommandPalette component ───────────────────────────────────── */

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const trapRef = useFocusTrap<HTMLDivElement>(open);
  const commands = useCommands(onClose);
  const navigate = useNavigate();

  // Debounce the query that hits the content-search API.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(id);
  }, [query]);
  const search = useSearch(debouncedQuery);
  const searchResults = search.data ?? [];

  function openResult(link: string) {
    onClose();
    navigate(link);
  }

  // Filter commands based on query
  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.section.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Group by section
  const sections = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const item of filtered) {
      const list = map.get(item.section) ?? [];
      list.push(item);
      map.set(item.section, list);
    }
    return map;
  }, [filtered]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedIndex(0);
      // Focus input after mount
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Clamp selected index
  useEffect(() => {
    if (selectedIndex >= filtered.length) {
      setSelectedIndex(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, selectedIndex]);

  // Keyboard nav within the list
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filtered[selectedIndex]) {
            filtered[selectedIndex].action();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filtered, selectedIndex, onClose],
  );

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Determine if we're on Mac for shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
  const modKey = isMac ? '⌘' : 'Ctrl';

  let flatIndex = -1;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -20 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed inset-x-0 top-[15%] z-50 mx-auto w-full max-w-lg"
          >
            <div
              ref={trapRef}
              className="overflow-hidden rounded-2xl border border-white/30 bg-white/95 shadow-2xl backdrop-blur-2xl"
              onKeyDown={handleKeyDown}
            >
              {/* Search input */}
              <div className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3">
                <Search className="size-5 text-neutral-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelectedIndex(0);
                  }}
                  placeholder="Search tasks, projects, chats, notifications…"
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400"
                />
                <kbd className="hidden rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 sm:inline">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div ref={listRef} className="max-h-80 overflow-y-auto py-2">
                {/* Content search results (tasks / projects / chats / notifications) */}
                {searchResults.length > 0 && (
                  <Fragment>
                    <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      Results
                    </div>
                    {searchResults.map((r, i) => {
                      const Icon = RESULT_ICON[r.type];
                      return (
                        <button
                          key={`res-${i}`}
                          type="button"
                          onClick={() => openResult(r.link)}
                          className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-neutral-100"
                        >
                          <Icon className="size-4 shrink-0 text-neutral-400" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-neutral-800">{r.title}</span>
                            {r.subtitle && (
                              <span className="block truncate text-xs text-neutral-400">{r.subtitle}</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </Fragment>
                )}

                {filtered.length === 0 ? (
                  searchResults.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-neutral-400">
                      {debouncedQuery.trim().length >= 2
                        ? search.isLoading
                          ? 'Searching…'
                          : 'No results found'
                        : 'No commands found'}
                    </div>
                  ) : null
                ) : (
                  Array.from(sections.entries()).map(([section, items]) => (
                    <Fragment key={section}>
                      <div className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                        {section}
                      </div>
                      {items.map((item) => {
                        flatIndex++;
                        const idx = flatIndex;
                        const selected = idx === selectedIndex;
                        const Icon = item.icon;

                        return (
                          <button
                            key={item.id}
                            type="button"
                            data-index={idx}
                            onClick={item.action}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            className={cn(
                              'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                              selected ? 'bg-pink-50/60' : 'hover:bg-neutral-50',
                            )}
                          >
                            <Icon
                              className={cn(
                                'size-4 shrink-0',
                                selected ? 'text-pink-500' : 'text-neutral-400',
                              )}
                            />
                            <div className="min-w-0 flex-1">
                              <p
                                className={cn(
                                  'text-sm font-medium',
                                  selected ? 'text-neutral-900' : 'text-neutral-700',
                                )}
                              >
                                {item.label}
                              </p>
                              {item.description && (
                                <p className="truncate text-xs text-neutral-400">
                                  {item.description}
                                </p>
                              )}
                            </div>
                            {item.shortcut && (
                              <kbd className="hidden shrink-0 rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 sm:inline">
                                {item.shortcut}
                              </kbd>
                            )}
                          </button>
                        );
                      })}
                    </Fragment>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-2">
                <div className="flex items-center gap-3 text-[10px] text-neutral-400">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-neutral-200 bg-neutral-50 px-1 py-0.5 text-[10px]">
                      &uarr;
                    </kbd>
                    <kbd className="rounded border border-neutral-200 bg-neutral-50 px-1 py-0.5 text-[10px]">
                      &darr;
                    </kbd>
                    navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-neutral-200 bg-neutral-50 px-1 py-0.5 text-[10px]">
                      &crarr;
                    </kbd>
                    select
                  </span>
                </div>
                <span className="text-[10px] text-neutral-300">
                  {modKey}+K to toggle
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
